import * as React from 'react';
import { useMemo } from 'react';
import {
    RoomPriceComponent,
    ProgramPriceComponent,
} from '@digitalopsdev/serviceops-reservations-components';
import type {
    ClusterDayAssignment,
    ClusterKind,
    DayRateSnapshot,
    ProgramRateSnapshot,
    RateSnapshotData,
    RateSnapshotListItem,
    RoomRateSnapshot,
} from './types';
import {
    buildClusterDayPairs,
    getClusterCurrentDays,
} from './clusterUtils';
import {
    buildProgramBookingStateForDialog,
    buildReservationStateForDialog,
    buildRoomBookingStateForDialog,
} from './dialogAdapter';

interface Props {
    cluster: {
        id: string;
        kind: ClusterKind;
        memberIds: string[];
        startDate: string;
        endDate: string;
        displayName: string;
    };
    snapshot: RateSnapshotListItem | undefined;
    targetCurrent: RateSnapshotData | null;
    assignment: ClusterDayAssignment | undefined;
}

interface DialogInputDay {
    dayRate: DayRateSnapshot;
    displayDate: Date;
}

function toInputs(
    pairs: { date: string; rate: DayRateSnapshot | null }[],
): DialogInputDay[] {
    return pairs
        .filter((p): p is { date: string; rate: DayRateSnapshot } => p.rate != null)
        .map(p => ({ dayRate: p.rate, displayDate: new Date(p.date + 'T00:00:00Z') }));
}

export function ClusterTotalPriceCell({ cluster, snapshot, targetCurrent, assignment }: Props) {
    const currentDays = useMemo(
        () => getClusterCurrentDays(cluster.memberIds, cluster.kind, targetCurrent),
        [cluster.memberIds, cluster.kind, targetCurrent],
    );

    const clusterDates = useMemo(() => currentDays.map(d => d.date), [currentDays]);

    const snapshotItems: (RoomRateSnapshot | ProgramRateSnapshot)[] = useMemo(() => {
        if (!snapshot) return [];
        return cluster.kind === 'ROOM' ? snapshot.data.rooms : snapshot.data.programs;
    }, [snapshot, cluster.kind]);

    const pairs = useMemo(
        () => buildClusterDayPairs(clusterDates, currentDays, assignment?.days ?? [], snapshotItems, cluster.kind),
        [clusterDates, currentDays, assignment, snapshotItems, cluster.kind],
    );

    if (currentDays.length === 0) {
        return <div className="text-xs text-gray-400">No current rates loaded</div>;
    }

    const isRoom = cluster.kind === 'ROOM';
    const typeKey = `cluster:${cluster.id}`;
    const reservationState = buildReservationStateForDialog(
        isRoom ? typeKey : null,
        isRoom ? cluster.displayName : null,
        !isRoom ? typeKey : null,
        !isRoom ? cluster.displayName : null,
    );

    const currentInputs = toInputs(pairs.map(p => ({ date: p.date, rate: p.current })));
    const afterInputs = toInputs(pairs.map(p => ({ date: p.date, rate: p.after })));

    const renderPrice = (inputs: DialogInputDay[]) => isRoom
        ? (
            <RoomPriceComponent
                reservationState={reservationState}
                roomBookingState={buildRoomBookingStateForDialog(typeKey, inputs)}
            />
        )
        : (
            <ProgramPriceComponent
                reservationState={reservationState}
                programBookingState={buildProgramBookingStateForDialog(typeKey, inputs)}
            />
        );

    return (
        <>
            <div className="text-xs flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                <span className="text-gray-500">Current:</span>
                {renderPrice(currentInputs)}
            </div>
            <div className="text-xs flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                <span className="text-gray-500">After:</span>
                {renderPrice(afterInputs)}
            </div>
        </>
    );
}
